// Adapted from eval/dashboard/internal_web/src/components/ui/Card.tsx (ATM-57): the heading wraps its controls below the title in a narrow window. Keep in step until ATM-60 shares it.
/**
 * The three containers every screen is built from.
 *
 * `Card` is a section: a white sheet with a hairline edge on the page. `CardHeading` is
 * the title-and-caption block that opens it. `CardPanel` is a box INSIDE a card — the
 * differential beside the transcript, the exchange it describes — drawn as a second white
 * sheet with the same hairline, because on a white card a sheet with no edge is invisible.
 *
 * A table is not put in a `CardPanel`. It prints straight onto the card, so its header
 * strip and its row tint run the card's full width, as in the design; the 28px inset every
 * card shares lives on its first and last cells instead (`DataTable`).
 *
 * There is no `Tile` component. The box a stat tile or a sentence dropdown stands in is
 * one class (`.card`, `.control`) applied by whatever already owns that element's box —
 * `StatTile`, `AgentPicker`, `Select tone="sentence"` — and a wrapper adding a second box
 * around each of them would break the fixed heights the design's rows are aligned on.
 *
 * The measurements are the mock's, taken off the nodes: a card's title starts 28px in
 * from its edge and 24px down, and its caption sits 24px above whatever follows. They live
 * here once so two screens cannot disagree about what a section looks like.
 */

import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

type CardProps = {
  children: ReactNode;
  className?: string;
};

/** A section: a white sheet with a hairline edge. */
export function Card({ children, className }: CardProps) {
  return <section className={cn("card", className)}>{children}</section>;
}

type HeadingProps = {
  title: string;
  /** The one line under the title that says what the section is of. */
  caption?: string;
  /** Controls that belong to the section, right-aligned on the title's baseline. */
  actions?: ReactNode;
  className?: string;
};

/** A section's title, its caption, and any controls that belong to it. */
export function CardHeading({ title, caption, actions, className }: HeadingProps) {
  return (
    // Wraps rather than squeezes: in a narrow window the controls drop below the title
    // instead of crushing the caption into a column one word wide.
    <header className={cn("flex flex-wrap items-start gap-x-4 gap-y-4 px-7 pb-6 pt-6", className)}>
      <div className="min-w-[14rem] flex-1">
        <h2 className="text-section text-ink">{title}</h2>
        {caption !== undefined ? (
          <p className="mt-0.5 text-caption text-ink-faint">{caption}</p>
        ) : null}
      </div>
      {actions !== undefined ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}

/** A box inside a card. */
export function CardPanel({ children, className }: CardProps) {
  return <div className={cn("panel", className)}>{children}</div>;
}
